import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { SlidersHorizontal } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ccCanOpen, ccCanOpenAnywhere } from './access';
import { c } from '../../theme';

// The ASC Dashboard's own navigation: one entry in the portal's sidebar, and
// the pages as tabs across the top — the shape the old app had, where the
// whole contact centre was one application rather than nine sidebar links.
//
// Three of those tabs carry a hover flyout, because the old app's nav does
// (App.jsx NAV_SUBVIEWS) and because Staffing and Repeat Callers are not
// pages of their own standing there — they are things you reach FROM a role's
// view, and Repeat Callers is reached from three of them. Promoting them to
// top-level tabs, as this bar did, made eleven peers out of what the old app
// presents as eight and their sub-views.
//
// A tab is drawn only when the server would serve that page to this person,
// by the same rule that gates the route and the data. Live Ops, Agents,
// Director, Manager, Supervisor, Team Performance and Outbound answer for any
// department the person holds; the rest are Support's. Settings is NOT a tab:
// it is configuration, and lives with the portal's other administration pages.
//
// Landing on /contact-center goes to Live Ops, as the old app's "/" did, and
// falls through to the first tab this person can open — a Collections
// supervisor should land on something rather than on a redirect to nowhere.

const REPEAT = { to: 'repeat-callers', label: 'Repeat Callers', view: 'repeat_callers' };

// The old app's order, which is NOT the order the server lists grants in and
// not alphabetical: Live Ops, Agents, the three role views, Email, VP, City
// Insights, Exec, Board, Outbound, Manager.
export const ASC_TABS = [
  { to: 'live', label: 'Live Ops', view: 'live_ops', anywhere: true },
  { to: 'agents', label: 'Agents', view: 'agents', anywhere: true },
  // One screen, three grants, as the old app had them (migration 075).
  // /director and /manager rendered the same component with the same props;
  // /supervisor rendered it ranked, on one pod. Which tabs appear depends on
  // the role AND the department: Manager is OBC's, Supervisor is Support's
  // and Collections'.
  {
    to: 'director',
    label: 'Director',
    view: 'director',
    anywhere: true,
    menu: 'Director Views',
    items: [
      { to: 'director', label: 'ASC Performance', view: 'director', anywhere: true },
      REPEAT,
      { to: 'staffing', label: 'Staffing', view: 'staffing' },
    ],
  },
  {
    to: 'supervisor',
    label: 'Supervisor',
    view: 'supervisor',
    anywhere: true,
    menu: 'Supervisor Views',
    // The old app's Supervisor flyout calls its own page "Team Performance".
    // Our /team route is the same page without a variant, kept for a
    // department still carrying the merged grant — so it is a tab only for
    // somebody who has it and no Supervisor grant, never both.
    items: [
      { to: 'supervisor', label: 'Team Performance', view: 'supervisor', anywhere: true },
      REPEAT,
    ],
  },
  { to: 'team', label: 'Team Performance', view: 'team_performance', anywhere: true, unless: 'supervisor' },
  { to: 'email', label: 'Email', view: 'email' },
  {
    to: 'vp',
    label: 'VP',
    view: 'vp',
    menu: 'VP Views',
    items: [
      { to: 'vp', label: 'VP Dashboard', view: 'vp' },
      REPEAT,
    ],
  },
  { to: 'city', label: 'City Insights', view: 'city' },
  { to: 'exec', label: 'Exec', view: 'exec' },
  // The old app's /board. "Leadership" is the view id migration 053 gave it
  // and the server still speaks it; the tab says what the old app says.
  { to: 'leadership', label: 'Board', view: 'leadership' },
  { to: 'outbound', label: 'Outbound', view: 'outbound', anywhere: true },
  { to: 'manager', label: 'Manager', view: 'manager', anywhere: true },
];

const canOpen = (user, t) => (t.anywhere ? ccCanOpenAnywhere(user, t.view) : ccCanOpen(user, t.view, 'support'));

/**
 * The tabs this person may open, in the old app's order, each with the
 * sub-views they may open.
 *
 * A parent whose own page is refused does NOT become a link to one of its
 * children under the parent's name — "Director" leading to Staffing would
 * claim a grant the person does not have. Its openable children are promoted
 * to tabs of their own instead, deduplicated, since Repeat Callers hangs off
 * three parents and a person may hold two of them.
 */
export function ascTabsFor(user) {
  const visible = ASC_TABS
    .filter((t) => !(t.unless && canOpen(user, { view: t.unless, anywhere: true })))
    .map((t) => {
      const items = (t.items || []).filter((i) => canOpen(user, i));
      const own = canOpen(user, t);
      // A menu of one is drawn only if that one is NOT the tab's own page —
      // "Director ▾ → ASC Performance" is a menu that repeats the tab. But a
      // parent whose own page is refused still has its survivors promoted
      // below, so they must not be discarded here.
      return { ...t, own, items, flyout: own && items.length > 1 ? items : [] };
    });

  // Everything a drawn flyout already offers. Worked out BEFORE promoting
  // anything, because Repeat Callers hangs off three parents: a person with
  // Supervisor and Repeat Callers but no Director would otherwise get it
  // twice — once promoted where Director would have been, once inside
  // Supervisor's menu.
  const covered = new Set();
  for (const t of visible) if (t.own) for (const i of t.flyout) covered.add(i.to);

  const out = [];
  const taken = new Set();
  for (const t of visible) {
    if (t.own) {
      taken.add(t.to);
      out.push({ ...t, items: t.flyout });
      continue;
    }
    for (const i of t.items) {
      if (i.to === t.to || covered.has(i.to) || taken.has(i.to)) continue;
      taken.add(i.to);
      out.push({ ...i, items: [] });
    }
  }
  return out;
}

// The flyout is PORTALED to the body and positioned fixed at the trigger.
// The tab bar scrolls horizontally, which makes it a scroll container on both
// axes, and a scroll container clips an absolutely-positioned child — the
// menu would mount and be invisible. The old app hit this and solved it the
// same way; doing it at the trigger's rect leaves the bar's scrolling alone.
function Flyout({ tab, onClose, onKeep, onLeave }) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const el = document.getElementById(`asc-tab-${tab.to}`);
    if (el) {
      const r = el.getBoundingClientRect();
      setPos({ left: Math.round(r.left), top: Math.round(r.bottom + 4) });
    }
  }, [tab.to]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!pos) return null;
  return createPortal(
    // Entering the card CANCELS the close the tab started. Without this the
    // menu shuts 120ms after the pointer leaves the tab — including while it
    // is crossing the 4px gap into the menu — so nothing in it can be clicked.
    <div ref={ref} role="menu" aria-label={tab.menu} style={{ ...s.flyout, left: pos.left, top: pos.top }}
      onMouseEnter={onKeep} onMouseLeave={onLeave}>
      <div style={s.flyoutHead}>{tab.menu}</div>
      {tab.items.map((i) => (
        <NavLink key={`${i.to}-${i.label}`} to={i.to} role="menuitem" onClick={onClose}
          className={({ isActive }) => `asc-sub${isActive ? ' asc-sub-on' : ''}`}>
          {i.label}
        </NavLink>
      ))}
    </div>,
    document.body,
  );
}

export default function AscShell() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const tabs = ascTabsFor(user);
  const [open, setOpen] = useState(null);
  const closeTimer = useRef(null);

  // Hover has to survive the gap between the tab and the card below it, or
  // the menu closes as the pointer crosses into it.
  const openNow = (to) => { clearTimeout(closeTimer.current); setOpen(to); };
  const closeSoon = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setOpen(null), 120); };
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  useEffect(() => { setOpen(null); }, [pathname]);

  // Nothing to show: the sidebar would not have drawn the link either.
  if (user && !tabs.length) return <Navigate to="/" replace />;
  if (pathname.replace(/\/$/, '').endsWith('/contact-center')) {
    return tabs.length ? <Navigate to={tabs[0].to} replace /> : null;
  }

  const active = tabs.find((t) => t.to === open && t.items.length);

  return (
    <div style={s.page}>
      <style>{css}</style>
      <div style={s.barRow}>
      <nav style={s.bar} aria-label="ASC Dashboard">
        {tabs.map((t) => (
          <NavLink key={t.to} id={`asc-tab-${t.to}`} to={t.to}
            aria-haspopup={t.items.length ? 'menu' : undefined}
            aria-expanded={t.items.length ? open === t.to : undefined}
            onMouseEnter={() => (t.items.length ? openNow(t.to) : setOpen(null))}
            onMouseLeave={t.items.length ? closeSoon : undefined}
            onFocus={() => (t.items.length ? openNow(t.to) : setOpen(null))}
            className={({ isActive }) => `asc-tab${isActive ? ' asc-tab-on' : ''}`}>
            {t.label}{t.items.length ? <span style={s.caret} aria-hidden="true">▾</span> : null}
          </NavLink>
        ))}
      </nav>
      {/* Settings is not a tab -- it configures the dashboard rather than
          being one of its views, and it follows the manage permission rather
          than a page grant. It sits on the bar anyway because the sidebar
          files "ASC Settings" under Mappings, inside a collapsible section,
          which is not where anyone pairing a TV or changing a threshold would
          think to look. Deliberately OUTSIDE the scrolling tab strip: the
          tabs overflow horizontally on a narrow window, and a control that
          scrolls out of sight is no more findable than one in the wrong
          menu. */}
      {user?.contactCenter?.canManage && (
        <NavLink to="/contact-center/settings" className="asc-settings" title="ASC Settings">
          <SlidersHorizontal size={13} aria-hidden="true" /> Settings
        </NavLink>
      )}
      </div>
      {active && (
        <Flyout tab={active} onClose={() => setOpen(null)}
          onKeep={() => openNow(active.to)} onLeave={closeSoon} />
      )}
      <Outlet />
    </div>
  );
}

// A tab bar needs :hover and an active underline, which inline styles cannot
// express — the pattern the portal uses elsewhere (Login, PageHeader).
const css = `
  .asc-tab {
    padding: 8px 2px; margin-right: 18px; font-size: 13px; font-weight: 550;
    color: ${c.textMuted}; text-decoration: none; white-space: nowrap;
    border-bottom: 2px solid transparent; transition: color .12s, border-color .12s;
  }
  .asc-tab:hover { color: ${c.text}; }
  .asc-settings {
    display: inline-flex; align-items: center; gap: 5px; flex-shrink: 0;
    padding: 5px 9px; margin-bottom: 3px; border-radius: 6px;
    font-size: 12px; font-weight: 550; white-space: nowrap;
    color: ${c.textMuted}; text-decoration: none;
    transition: color .12s, background .12s;
  }
  .asc-settings:hover { color: ${c.navy}; background: ${c.surfaceSunk}; }
  .asc-tab-on { color: ${c.navy}; border-bottom-color: ${c.brand}; }
  .asc-sub {
    display: block; padding: 7px 12px; border-radius: 6px; font-size: 13px;
    color: ${c.textStrong}; text-decoration: none; white-space: nowrap;
  }
  .asc-sub:hover { background: ${c.surfaceSunk}; color: ${c.text}; }
  .asc-sub-on { color: ${c.navy}; font-weight: 650; }
  @media (max-width: 720px) { .asc-tab { margin-right: 12px; font-size: 12.5px; } }
`;

const s = {
  page: { display: 'flex', flexDirection: 'column', gap: '14px' },
  // The border moved from the tab strip to the row, so it runs the full width
  // underneath both the tabs and Settings rather than stopping where the
  // scrolling strip ends.
  barRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    borderBottom: `1px solid ${c.border}`,
  },
  bar: {
    display: 'flex', alignItems: 'center', overflowX: 'auto',
    padding: '0 2px', flex: 1, minWidth: 0,
  },
  caret: { marginLeft: '4px', fontSize: '9px', verticalAlign: 'middle' },
  flyout: {
    position: 'fixed', zIndex: 60, minWidth: '190px', background: c.surface,
    border: `1px solid ${c.border}`, borderRadius: '10px', padding: '6px',
    boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
  },
  flyoutHead: {
    fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
    color: c.textFaint, padding: '4px 12px 6px',
  },
};
